/**
 * The portal Work dialog's record key: `plan:task:date[:rescheduledDate]`. A forecast row's own `_id` is a one-way
 * hash nothing can read back, so the dialog is addressed by the facts the Work task read needs instead; the key
 * survives a reload and a pasted link, and parses straight into that read's request.
 */
import { WorkTaskScheduleRow, WorkTaskScheduleTaskRequest } from '@jms/model';

const LOCAL_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The key for a Work row; null when the row lacks its plan, task or scheduled date, so it opens nothing. */
export function workTaskOccurrenceKey(row: Pick<WorkTaskScheduleRow, 'taskId' | 'servicePlan' | 'date' | 'rescheduledDate'> | null | undefined): string | null {

  const planId = row?.servicePlan?._id;
  if (!planId || !row?.taskId || !row.date) return null;
  if (!LOCAL_DATE.test(row.date)) return null;
  const parts = [planId, row.taskId, row.date];
  if (row.rescheduledDate && row.rescheduledDate !== row.date) parts.push(row.rescheduledDate);

  return parts.join(':');
}

/** The Work task read's request for a key; null for anything `workTaskOccurrenceKey` did not mint. */
export function parseWorkTaskOccurrenceKey(key: string | null | undefined): WorkTaskScheduleTaskRequest | null {

  const parts = key?.split(':') ?? [];
  if (parts.length !== 3 && parts.length !== 4) return null;
  const [servicePlanId, taskId, date, rescheduledDate] = parts;
  if (!servicePlanId || !taskId) return null;
  if (!LOCAL_DATE.test(date)) return null;
  if (rescheduledDate !== undefined && !LOCAL_DATE.test(rescheduledDate)) return null;

  return {
    taskId,
    servicePlanId,
    date,
    ...(rescheduledDate ? { rescheduledDate } : {}),
  };
}
